<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  step: {
    type: Object,
    required: true,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  // União de templates de todos os inboxes elegíveis — usada só quando o
  // passo não tem uma inbox específica escolhida (ver templatesForInbox).
  templates: {
    type: Array,
    default: () => [],
  },
  // Mapa inbox_id -> templates daquela inbox. Quando presente e o passo
  // tem uma inbox específica escolhida, restringe o seletor de template
  // aos templates que realmente existem nela (evita a combinação
  // template-de-A-enviado-por-B que o processor descarta silenciosamente).
  templatesByInbox: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();

const VARIABLES = [
  '{{primeiro_nome}}',
  '{{nome}}',
  '{{telefone}}',
  '{{email}}',
  '{{empresa}}',
];

// Atributos oferecidos para as variáveis do template. Os valores viajam para o
// backend (Funnels::TemplateVariableResolver), que os traduz para os mesmos
// tokens do modo Texto — uma convenção só.
const CONTACT_ATTRIBUTES = [
  'first_name',
  'name',
  'phone_number',
  'email',
  'company',
];

const BODY_VARIABLE_PATTERN = /\{\{\s*(\d+)\s*\}\}/g;

const localStep = reactive({ ...props.step });
const days = ref(Math.floor((Number(props.step.delay_hours) || 0) / 24));
const hours = ref((Number(props.step.delay_hours) || 0) % 24);
// Passo "na hora": entregue no evento de entrada na etapa
// (Funnels::InstantStageFollowUpJob), não pelo cron da régua. Espera não existe
// nesse modo, então os campos de dias/horas sequer aparecem.
const onStageEnter = ref(!!props.step.on_stage_enter);
const messageTextareaRef = ref(null);

// Templates candidatos ao passo atual. Com uma inbox específica escolhida
// (inbox_id != null) e o mapa por-inbox disponível, restringe à lista
// daquela inbox — mesmo que vazia. Sem inbox específica ("Mesma caixa da
// conversa") ou sem o mapa disponível, cai na união (comportamento antigo).
const templatesForInbox = computed(() => {
  if (localStep.inbox_id == null || !props.templatesByInbox) {
    return props.templates;
  }
  return props.templatesByInbox[localStep.inbox_id] || [];
});

// Passo salvo como template sem templates disponíveis pro seu escopo atual
// (ex.: template excluído na Meta, ou inbox escolhida não tem nenhum): cai
// pra texto pra não renderizar um select vazio.
if (localStep.message_type === 'template' && !templatesForInbox.value.length) {
  localStep.message_type = 'text';
}

// Trocar a inbox do passo pode invalidar o template já escolhido (ele só
// existia na inbox anterior). Sem isso o Save salvaria uma combinação
// impossível que o processor descarta silenciosamente em produção.
watch(
  () => localStep.inbox_id,
  () => {
    if (localStep.message_type !== 'template') return;
    if (!templatesForInbox.value.length) {
      localStep.message_type = 'text';
      localStep.template_name = '';
      return;
    }
    const stillValid = templatesForInbox.value.some(
      template => template.name === localStep.template_name
    );
    if (!stillValid) {
      localStep.template_name = '';
    }
  }
);

const isVisible = ref(false);
onMounted(() => {
  nextTick(() => {
    isVisible.value = true;
  });
});

const isTemplate = computed(() => localStep.message_type === 'template');

const typeOptions = computed(() => {
  const options = [{ value: 'text', label: t('FUNNEL_TIMELINE.TYPE_TEXT') }];
  if (templatesForInbox.value.length) {
    options.push({
      value: 'template',
      label: t('FUNNEL_TIMELINE.TYPE_TEMPLATE'),
    });
  }
  return options;
});

const selectedTemplate = computed(() =>
  templatesForInbox.value.find(
    template => template.name === localStep.template_name
  )
);

const templateBody = computed(() => {
  const body = (selectedTemplate.value?.components || []).find(
    component => (component.type || '').toUpperCase() === 'BODY'
  );
  return body?.text || '';
});

// Índices {{n}} declarados no CORPO, sem repetir. Cabeçalho e botões ficam de
// fora de propósito: nenhum template da base usa variável neles.
const templateVariables = computed(() => {
  const found = [...templateBody.value.matchAll(BODY_VARIABLE_PATTERN)].map(
    match => match[1]
  );
  return [...new Set(found)];
});

// Sem mapeamento a Meta recusa o envio com "#132000 Number of parameters does
// not match the expected number of params". Por isso o Salvar espera.
const missingVariables = computed(() =>
  templateVariables.value.filter(index => {
    const mapping = localStep.template_variables?.[index];
    return !mapping?.source || !String(mapping.value || '').trim();
  })
);

const ensureVariableSlots = () => {
  const current = { ...(localStep.template_variables || {}) };
  templateVariables.value.forEach(index => {
    if (!current[index]) {
      current[index] = { source: 'attribute', value: 'name', fallback: '' };
    }
  });
  // Trocar de template pode deixar mapeamento órfão de {{n}} que não existe
  // mais — ele iria junto no save e confundiria a leitura do passo.
  Object.keys(current).forEach(index => {
    if (!templateVariables.value.includes(index)) delete current[index];
  });
  localStep.template_variables = current;
};

watch(
  () => [localStep.template_name, localStep.message_type],
  () => {
    if (isTemplate.value) ensureVariableSlots();
  },
  { immediate: true }
);

const ATTRIBUTE_LABELS = {
  first_name: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_FIRST_NAME'),
  name: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_NAME'),
  phone_number: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_PHONE_NUMBER'),
  email: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_EMAIL'),
  company: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_COMPANY'),
};

const attributeLabel = attribute =>
  (ATTRIBUTE_LABELS[attribute] || ATTRIBUTE_LABELS.name)();

// Montado no script: chaves duplas dentro de interpolação confundem o parser
// de template do Vue.
const variableLabel = index => `{{${index}}}`;

const previewValue = index => {
  const mapping = localStep.template_variables?.[index];
  if (!mapping) return `{{${index}}}`;
  return mapping.source === 'attribute'
    ? attributeLabel(mapping.value || 'name')
    : mapping.value || `{{${index}}}`;
};

const templatePreview = computed(() =>
  templateBody.value.replace(BODY_VARIABLE_PATTERN, (_, index) =>
    previewValue(index)
  )
);

const canSave = computed(() => {
  if (!isTemplate.value) return !!localStep.content?.trim();
  return !!localStep.template_name && missingVariables.value.length === 0;
});

const insertVariable = variable => {
  const el = messageTextareaRef.value;
  const currentContent = localStep.content || '';
  const start = el?.selectionStart ?? currentContent.length;
  const end = el?.selectionEnd ?? currentContent.length;

  localStep.content =
    currentContent.slice(0, start) + variable + currentContent.slice(end);

  nextTick(() => {
    if (!el) return;
    const cursor = start + variable.length;
    el.focus();
    el.setSelectionRange(cursor, cursor);
  });
};

const handleSave = () => {
  if (!canSave.value) return;
  emit('save', {
    ...localStep,
    on_stage_enter: onStageEnter.value,
    delay_hours: onStageEnter.value
      ? 0
      : (Number(days.value) || 0) * 24 + (Number(hours.value) || 0),
  });
};

const handleCancel = () => {
  emit('cancel');
};
</script>

<template>
  <div
    class="grid overflow-hidden transition-all duration-200 ease-in-out"
    :class="
      isVisible ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
    "
  >
    <div class="min-h-0 space-y-3 overflow-hidden p-3">
      <label
        class="flex cursor-pointer items-start gap-2.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
      >
        <input
          v-model="onStageEnter"
          type="checkbox"
          data-testid="follow-up-step-editor-on-stage-enter"
          class="mt-0.5 size-4 shrink-0 accent-n-brand"
        />
        <span class="flex flex-col gap-0.5">
          <span
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
          >
            <span class="i-lucide-zap size-3.5 text-n-brand" />
            {{ t('FUNNEL_TIMELINE.ON_STAGE_ENTER') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('FUNNEL_TIMELINE.ON_STAGE_ENTER_HINT') }}
          </span>
        </span>
      </label>

      <div v-if="!onStageEnter" class="grid grid-cols-2 gap-3">
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ t('FUNNEL_TIMELINE.DELAY_DAYS') }}
          </label>
          <input
            v-model.number="days"
            type="number"
            min="0"
            data-testid="follow-up-step-editor-days"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ t('FUNNEL_TIMELINE.DELAY_HOURS') }}
          </label>
          <input
            v-model.number="hours"
            type="number"
            min="0"
            data-testid="follow-up-step-editor-hours"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="relative w-full">
          <select
            v-model="localStep.message_type"
            data-testid="follow-up-step-editor-type"
            class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option
              v-for="option in typeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          />
        </div>
        <div class="relative w-full">
          <select
            v-model="localStep.inbox_id"
            data-testid="follow-up-step-editor-inbox"
            class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option :value="null">
              {{ t('FUNNEL_TIMELINE.SAME_INBOX') }}
            </option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          />
        </div>
      </div>

      <div v-if="isTemplate" class="relative w-full">
        <select
          v-model="localStep.template_name"
          data-testid="follow-up-step-editor-template"
          class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
        >
          <option
            v-for="template in templatesForInbox"
            :key="template.name"
            :value="template.name"
          >
            {{ template.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
        />
      </div>

      <div v-else class="space-y-1.5">
        <textarea
          ref="messageTextareaRef"
          v-model="localStep.content"
          rows="4"
          data-testid="follow-up-step-editor-message"
          class="w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          :placeholder="t('FUNNEL_TIMELINE.MESSAGE')"
        />
        <div class="flex flex-wrap items-center gap-1.5">
          <span class="text-xs text-n-slate-10">
            {{ t('FUNNEL_TIMELINE.VARIABLES_HINT') }}
          </span>
          <button
            v-for="variable in VARIABLES"
            :key="variable"
            type="button"
            :data-testid="`follow-up-step-editor-chip-${variable.replace(/[{}]/g, '')}`"
            class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs hover:bg-n-brand/20"
            @click="insertVariable(variable)"
          >
            {{ variable }}
          </button>
        </div>
      </div>

      <div v-if="isTemplate && templateVariables.length" class="space-y-2">
        <p class="text-xs font-medium text-n-slate-11">
          {{ t('FUNNEL_TIMELINE.TEMPLATE_VARIABLES_TITLE') }}
        </p>
        <div
          v-for="index in templateVariables"
          :key="index"
          class="grid grid-cols-[3rem_1fr_1fr] items-center gap-2"
          :data-testid="`follow-up-step-editor-variable-${index}`"
        >
          <span class="font-mono text-xs text-n-slate-11">
            {{ variableLabel(index) }}
          </span>
          <select
            v-model="localStep.template_variables[index].source"
            :data-testid="`follow-up-step-editor-variable-source-${index}`"
            class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option value="attribute">
              {{ t('FUNNEL_TIMELINE.TEMPLATE_SOURCE_ATTRIBUTE') }}
            </option>
            <option value="text">
              {{ t('FUNNEL_TIMELINE.TEMPLATE_SOURCE_TEXT') }}
            </option>
          </select>
          <select
            v-if="localStep.template_variables[index].source === 'attribute'"
            v-model="localStep.template_variables[index].value"
            :data-testid="`follow-up-step-editor-variable-value-${index}`"
            class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option
              v-for="attribute in CONTACT_ATTRIBUTES"
              :key="attribute"
              :value="attribute"
            >
              {{ attributeLabel(attribute) }}
            </option>
          </select>
          <input
            v-else
            v-model="localStep.template_variables[index].value"
            :data-testid="`follow-up-step-editor-variable-value-${index}`"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="t('FUNNEL_TIMELINE.TEMPLATE_TEXT_PLACEHOLDER')"
          />
          <span />
          <input
            v-if="localStep.template_variables[index].source === 'attribute'"
            v-model="localStep.template_variables[index].fallback"
            :data-testid="`follow-up-step-editor-variable-fallback-${index}`"
            class="col-span-2 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="t('FUNNEL_TIMELINE.TEMPLATE_FALLBACK_PLACEHOLDER')"
          />
        </div>
        <p
          class="whitespace-pre-line rounded-lg bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
          data-testid="follow-up-step-editor-template-preview"
        >
          {{ templatePreview }}
        </p>
      </div>

      <div
        class="flex items-center justify-end gap-2 border-t border-n-weak pt-3"
      >
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          data-testid="follow-up-step-editor-cancel"
          :label="t('FUNNEL_TIMELINE.CANCEL')"
          @click="handleCancel"
        />
        <Button
          variant="solid"
          color="blue"
          size="sm"
          data-testid="follow-up-step-editor-save"
          :disabled="!canSave"
          :label="t('FUNNEL_TIMELINE.SAVE')"
          @click="handleSave"
        />
      </div>
    </div>
  </div>
</template>
